/* QLS Search Inspiration v31
   Lightweight recent-search storage plus two selectable mobile opening modes.
   No libraries, observers, timers that loop, or network requests are added here. */
(function () {
  'use strict';

  var STORAGE_KEY = 'qls_recent_searches_v1';
  var DEFAULT_LIMIT = 5;
  var TAP_GUARD_MS = 280;
  var MOBILE_QUERY = window.matchMedia ? window.matchMedia('(max-width: 768px)') : null;
  var pendingLink = null;
  var ignoreClickUntil = 0;
  var panelOpenedAtScrollY = 0;
  var scrollDismissBlockedUntil = 0;
  var SCROLL_DISMISS_DISTANCE = 18;

  function armTapGuard(root) {
    if (!root || !isMobile()) return;
    if (root._qlsTapGuardTimer) window.clearTimeout(root._qlsTapGuardTimer);
    root.classList.add('qls-search-inspiration--tap-guard');
    ignoreClickUntil = Math.max(ignoreClickUntil, Date.now() + TAP_GUARD_MS);
    root._qlsTapGuardTimer = window.setTimeout(function () {
      root.classList.remove('qls-search-inspiration--tap-guard');
      root._qlsTapGuardTimer = null;
    }, TAP_GUARD_MS);
  }

  function normalise(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function isMobile() {
    var small = MOBILE_QUERY ? MOBILE_QUERY.matches : window.innerWidth <= 768;
    var touch = ('ontouchstart' in window) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
    return !!(small && touch);
  }

  function isSearchInput(element) {
    return !!(element && element.matches && element.matches('input[name="q"], input[type="search"], .search__input'));
  }

  function rootOf(element) {
    if (!element || !element.closest) return null;
    var root = element.closest('.header__search .search') || element.closest('.search');
    return root && root.querySelector('.qls-search-inspiration') ? root : null;
  }

  function inputOf(root) {
    return root ? root.querySelector('input[name="q"], input[type="search"], .search__input') : null;
  }

  function panelOf(root) {
    return root ? root.querySelector('.qls-search-inspiration') : null;
  }

  function modeOf(root) {
    var panel = panelOf(root);
    return panel && panel.getAttribute('data-qls-mobile-search-mode') === 'inspiration_first'
      ? 'inspiration_first'
      : 'instant_typing';
  }

  function currentView(root) {
    return root && root.classList.contains('qls-search-inspiration--compact') ? 'compact' : 'full';
  }

  function restoreInput(input) {
    if (!input) return;
    input.removeAttribute('readonly');
    input.removeAttribute('data-qls-mobile-panel-readonly');
  }

  function setView(root, view) {
    if (!root) return;
    var compact = view === 'compact';
    root.classList.toggle('qls-search-inspiration--compact', compact);
    root.classList.toggle('qls-search-inspiration--full', !compact);
    var panel = panelOf(root);
    if (panel) panel.setAttribute('data-qls-current-view', compact ? 'compact' : 'full');
  }

  function openPanel(root, view) {
    if (!root) return;

    var wasOpen = root.classList.contains('qls-search-inspiration--mobile-open');
    renderAll();
    root.classList.add('qls-search-inspiration--force-open', 'qls-search-inspiration--mobile-open');

    if (isMobile() && !wasOpen) {
      panelOpenedAtScrollY = window.scrollY || window.pageYOffset || 0;
      // Ignore the viewport movement caused by the iPhone keyboard opening.
      scrollDismissBlockedUntil = Date.now() + 2800;
    }
    root.classList.remove('qls-search-inspiration--escape-closed');
    setView(root, view || currentView(root));
    var input = inputOf(root);
    if (input) input.setAttribute('aria-expanded', 'true');
  }

  function closePanel(root) {
    if (!root) return;
    root.classList.remove(
      'qls-search-inspiration--force-open',
      'qls-search-inspiration--mobile-open',
      'qls-search-inspiration--keyboard-next',
      'qls-search-inspiration--compact',
      'qls-search-inspiration--full'
    );
    var input = inputOf(root);
    restoreInput(input);
    if (input && !normalise(input.value)) input.setAttribute('aria-expanded', 'false');
  }

  function showFull(root) {
    if (!root) return;
    var input = inputOf(root);
    restoreInput(input);
    openPanel(root, 'full');
    root.classList.add('qls-search-inspiration--keyboard-next');
    if (input && document.activeElement === input && input.blur) input.blur();
  }

  function showCompact(root) {
    if (!root) return;
    restoreInput(inputOf(root));
    root.classList.remove('qls-search-inspiration--keyboard-next');
    openPanel(root, 'compact');
  }

  function stop(event) {
    if (!event) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
  }

  function readList() {
    try {
      var value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  }

  function writeList(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {}
  }

  function maxLimit() {
    var max = DEFAULT_LIMIT;
    document.querySelectorAll('[data-qls-recent-searches]').forEach(function (container) {
      var value = parseInt(container.getAttribute('data-limit') || DEFAULT_LIMIT, 10);
      if (!isNaN(value) && value > max) max = value;
    });
    return max;
  }

  function saveSearch(term) {
    term = normalise(term);
    if (!term || term.length < 2) return;
    var list = readList().filter(function (item) {
      return item.toLowerCase() !== term.toLowerCase();
    });
    list.unshift(term);
    writeList(list.slice(0, maxLimit()));
    renderAll();
  }

  function removeSearch(term) {
    term = normalise(term);
    writeList(readList().filter(function (item) {
      return item.toLowerCase() !== term.toLowerCase();
    }));
    renderAll();
  }

  function clearSearches() {
    writeList([]);
    renderAll();
  }

  function recentChip(term) {
    var chip = document.createElement('span');
    chip.className = 'qls-search-inspiration__recent-chip';

    var link = document.createElement('a');
    link.className = 'qls-search-inspiration__recent-link';
    link.href = '/search?q=' + encodeURIComponent(term);
    link.setAttribute('data-qls-recent-link', term);
    link.innerHTML = '<span aria-hidden="true" class="qls-search-inspiration__search-dot">⌕</span><span></span>';
    link.querySelector('span:last-child').textContent = term;

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'qls-search-inspiration__recent-remove';
    remove.setAttribute('data-qls-recent-remove', term);
    remove.setAttribute('aria-label', 'Remove recent search: ' + term);
    remove.textContent = '×';

    chip.appendChild(link);
    chip.appendChild(remove);
    return chip;
  }

  function renderAll() {
    var list = readList();
    document.querySelectorAll('[data-qls-recent-searches]').forEach(function (container) {
      var limit = parseInt(container.getAttribute('data-limit') || DEFAULT_LIMIT, 10);
      if (isNaN(limit) || limit < 1) limit = DEFAULT_LIMIT;
      var target = container.querySelector('[data-qls-recent-list]');
      if (!target) return;
      target.innerHTML = '';
      list.slice(0, limit).forEach(function (term) {
        target.appendChild(recentChip(term));
      });
      container.hidden = list.length === 0;
    });
  }

  function searchTermFromHref(href) {
    try {
      var url = new URL(href, window.location.origin);
      return /\/search\/?$/i.test(url.pathname) ? normalise(url.searchParams.get('q')) : '';
    } catch (error) {
      return '';
    }
  }

  function saveHref(href) {
    var term = searchTermFromHref(href);
    if (term) saveSearch(term);
  }

  function panelLink(element) {
    if (!element || !element.closest) return null;
    return element.closest('.qls-search-inspiration a[href]');
  }

  function navigate(href) {
    if (!href) return;
    var destination;
    try {
      destination = new URL(href, window.location.origin).href;
    } catch (error) {
      destination = href;
    }
    if (destination === window.location.href && window.location.reload) window.location.reload();
    else window.location.assign(destination);
  }

  function keepOpen(control) {
    var root = rootOf(control);
    if (!root) return;
    if (isMobile()) {
      openPanel(root, currentView(root));
      return;
    }
    root.classList.add('qls-search-inspiration--force-open');
    var input = inputOf(root);
    if (input) input.focus();
    window.setTimeout(function () {
      root.classList.remove('qls-search-inspiration--force-open');
    }, 180);
  }

  function useRecentControl(control, event) {
    if (!control) return false;
    if (event) stop(event);
    var root = rootOf(control);
    armTapGuard(root);
    if (control.matches('[data-qls-recent-clear]')) clearSearches();
    else if (control.matches('[data-qls-recent-remove]')) removeSearch(control.getAttribute('data-qls-recent-remove'));
    else return false;
    keepOpen(control);
    return true;
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches('form[role="search"], form[action*="/search"]')) return;
    var input = form.querySelector('input[name="q"], input[type="search"], .search__input');
    if (input) saveSearch(input.value);
  }, true);

  document.addEventListener('pointerdown', function (event) {
    if (!isMobile() || !event.target || !event.target.closest) return;
    var target = event.target;

    // Product type is a separate control. It must not activate the text-input
    // inspiration panel or mobile keyboard flow.
    var productTypeControl = target.closest('.js-search-product-types, custom-select#product_types');
    if (productTypeControl) {
      var productTypeRoot = rootOf(productTypeControl);
      if (productTypeRoot) closePanel(productTypeRoot);
      return;
    }

    var link = panelLink(target);
    if (link) {
      pendingLink = {
        href: link.href,
        x: event.clientX || 0,
        y: event.clientY || 0,
        moved: false
      };
      var linkRoot = rootOf(link);
      if (linkRoot) openPanel(linkRoot, currentView(linkRoot));
      return;
    }

    var input = isSearchInput(target) ? target : null;
    if (input) {
      var root = rootOf(input);
      if (!root || normalise(input.value)) return;
      restoreInput(input);

      if (modeOf(root) === 'instant_typing') {
        root.classList.remove('qls-search-inspiration--keyboard-next');
        return; // Native focus opens the keyboard; focusin applies compact mode.
      }

      if (root.classList.contains('qls-search-inspiration--keyboard-next')) {
        root.classList.remove('qls-search-inspiration--keyboard-next');
        return; // Second tap remains native.
      }

      event.preventDefault();
      event.stopPropagation();
      showFull(root);
      armTapGuard(root);
      return;
    }
  }, true);

  document.addEventListener('pointermove', function (event) {
    if (!pendingLink) return;
    if (Math.abs((event.clientX || 0) - pendingLink.x) > 12 || Math.abs((event.clientY || 0) - pendingLink.y) > 12) {
      pendingLink.moved = true;
    }
  }, true);

  document.addEventListener('pointercancel', function () {
    pendingLink = null;
  }, true);

  document.addEventListener('pointerup', function (event) {
    if (!isMobile() || !pendingLink) return;
    var pending = pendingLink;
    pendingLink = null;
    if (pending.moved) return;
    stop(event);
    ignoreClickUntil = Date.now() + 700;
    saveHref(pending.href);
    navigate(pending.href);
  }, true);

  document.addEventListener('focusin', function (event) {
    if (event.target && event.target.closest && event.target.closest('.js-search-product-types, custom-select#product_types')) {
      var productTypeRoot = rootOf(event.target);
      if (productTypeRoot) closePanel(productTypeRoot);
      return;
    }
    if (!isSearchInput(event.target)) return;
    window.QLSMobileInputFocusGuardUntil = Date.now() + 3000;
    scrollDismissBlockedUntil = Date.now() + 3000;
    renderAll();
    if (!isMobile()) return;
    var root = rootOf(event.target);
    if (root && !normalise(event.target.value)) {
      showCompact(root);
      armTapGuard(root);
    }
  }, true);

  document.addEventListener('input', function (event) {
    if (!isSearchInput(event.target)) return;
    var root = rootOf(event.target);
    if (!root) return;
    if (normalise(event.target.value)) {
      closePanel(root); // Predictive search takes over once text exists.
      return;
    }
    if (isMobile() && document.activeElement === event.target) showCompact(root);
  }, true);

  document.addEventListener('mousedown', function (event) {
    if (isMobile() || !event.target || !event.target.closest) return;

    var link = panelLink(event.target);
    if (link) {
      /*
        On desktop the panel is displayed while the search input has focus.
        A normal mousedown transfers focus to the anchor before click fires,
        which can hide/remove the panel and cancel the navigation. Keep focus
        on the search input until the click handler completes.
      */
      event.preventDefault();

      var linkRoot = rootOf(link);
      if (linkRoot) {
        linkRoot.classList.add('qls-search-inspiration--force-open');
      }
      return;
    }

    var control = event.target.closest('[data-qls-recent-clear], [data-qls-recent-remove]');
    if (control) event.preventDefault();
  }, true);

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;

    var reset = event.target.closest('.js-search-reset');
    if (reset && isMobile()) armTapGuard(rootOf(reset));

    if (Date.now() < ignoreClickUntil && event.target.closest('.qls-search-inspiration')) {
      stop(event);
      return;
    }

    var browse = event.target.closest('[data-qls-browse-full]');
    if (browse) {
      stop(event);
      var browseRoot = rootOf(browse);
      showFull(browseRoot);
      armTapGuard(browseRoot);
      return;
    }

    var control = event.target.closest('[data-qls-recent-clear], [data-qls-recent-remove]');
    if (control && useRecentControl(control, event)) return;

    if (isMobile()) {
      var openRoots = document.querySelectorAll('.search.qls-search-inspiration--mobile-open');
      if (openRoots.length && !event.target.closest('.search.qls-search-inspiration--mobile-open')) {
        stop(event);
        openRoots.forEach(closePanel);
        return;
      }
    }

    var link = panelLink(event.target);
    if (!link) return;

    saveHref(link.href);

    if (!isMobile()) {
      /*
        Desktop links are navigated explicitly so click-outside/focus handlers
        cannot close the panel before the browser follows the destination.
      */
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      navigate(link.href);
      return;
    }

    /* Mobile pointer navigation is handled earlier by pointerup. */
  }, true);

  document.addEventListener('keydown', function (event) {
    if ((event.key === 'Enter' || event.key === 'Return') && isSearchInput(event.target)) {
      saveSearch(event.target.value);
      return;
    }
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    var root = rootOf(event.target);
    if (!root) return;
    event.preventDefault();
    event.stopPropagation();
    closePanel(root);
    root.classList.add('qls-search-inspiration--escape-closed');
    if (document.activeElement && root.contains(document.activeElement) && document.activeElement.blur) {
      document.activeElement.blur();
    }
    window.setTimeout(function () {
      root.classList.remove('qls-search-inspiration--escape-closed');
    }, 300);
  }, true);

  function closeOpenPanelsOnMobileScroll() {
    if (!isMobile() || Date.now() < scrollDismissBlockedUntil) return;

    var openRoots = document.querySelectorAll('.search.qls-search-inspiration--mobile-open');
    if (!openRoots.length) return;

    /* iOS moves the visual viewport and may alter page scroll while opening
       the keyboard. Never treat that movement as an intentional dismissal. */
    var focusedInput = Array.prototype.some.call(openRoots, function (root) {
      var input = inputOf(root);
      return input && document.activeElement === input;
    });
    if (focusedInput || Date.now() < Number(window.QLSMobileInputFocusGuardUntil || 0)) return;

    var currentScrollY = window.scrollY || window.pageYOffset || 0;
    if (Math.abs(currentScrollY - panelOpenedAtScrollY) < SCROLL_DISMISS_DISTANCE) return;

    openRoots.forEach(function (root) {
      closePanel(root);
    });

    panelOpenedAtScrollY = currentScrollY;
    scrollDismissBlockedUntil = Date.now() + 180;
  }

  window.addEventListener('scroll', closeOpenPanelsOnMobileScroll, { passive: true });

  function boot() {
    var current = searchTermFromHref(window.location.href);
    if (current) saveSearch(current);
    renderAll();
  }

  window.addEventListener('pageshow', boot);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) renderAll();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
